// Video-wall transaction notifications are keyed by lane (gateId), so every camera on a lane -
// primary and auxiliary - shares one notification. Consumers must therefore look them up by the
// camera's gateId, never its gateCameraId. This helper centralizes that lookup and adds a dev-only
// guard: if a camera config reaches the map without a gateId, the notification silently resolves to
// undefined and the tile shows no assignment/badge - a subtle, symptomless bug. In dev we warn once
// per offending camera so it surfaces during development instead of in production.

const warned = new Set();

/**
 * Resolve a lane's notification from the gateId-keyed notifications map.
 *
 * @param {object} notifications the `videoWallTransactionNotifications` map (keyed by gateId)
 * @param {object} source a camera-shaped object; its gateId is read from `source.gateId`,
 *                         falling back to `source.camConfig.gateId` (some items nest it there)
 * @returns {object|undefined} the lane notification, or undefined when there is none
 */
export const getLaneNotification = (notifications, source) => {
    const gateId = source?.gateId ?? source?.camConfig?.gateId;

    // Warn on ANY missing gateId. The previous guard also required gateCameraId to be present, so a camera
    // carrying neither id - the most lost object of all - passed completely silently. The key combines both
    // ids because `warned` is module level and never cleared: keying it too coarsely would suppress a
    // genuinely different cause for the rest of the dev session (e.g. across an HMR reload).
    if (import.meta.env?.DEV && gateId == null) {
        const key = `${source?.id ?? '?'}:${source?.gateCameraId ?? '?'}`;
        if (!warned.has(key)) {
            warned.add(key);
            console.warn(
                `[videoWall] notification lookup missing gateId (id=${source?.id ?? 'n/a'}, ` +
                    `gateCameraId=${source?.gateCameraId ?? 'n/a'}); notifications are keyed by lane (gateId) ` +
                    'and will not resolve for this camera.'
            );
        }
    }

    return notifications?.[gateId];
};

/**
 * Whether a lane is waiting for someone to take it: its detection arrived, no assignment followed, and it is
 * not fading out after a finished transaction. A lane pinned (sticky lane) never waits: the backend hands
 * everything on it to the agent who pinned it straight away, whatever their desk limit.
 *
 * @param {object} notification a lane notification (see getLaneNotification)
 * @param {string|null} [pinnedByUsername] username of the agent the lane is pinned to, if any
 * @returns {boolean}
 */
export const isLaneUnassigned = (notification, pinnedByUsername = null) =>
    !pinnedByUsername && notification?.transactions?.length === 0 && !notification?.finishedAt;

/**
 * Whether a lane is being processed: at least one assignment, and it is not fading out after its last transaction.
 *
 * @param {object} notification a lane notification (see getLaneNotification)
 * @returns {boolean}
 */
export const isVideoWallTransactionActive = (notification) => (notification?.transactions?.length ?? 0) > 0 && !notification?.finishedAt;

/**
 * Whether a lane is pinned (sticky lane) to another agent. The backend rejects a manual assign on such a lane.
 *
 * @param {string|null} pinnedByUsername username of the agent the lane is pinned to, if any
 * @param {string} activeUser username of the logged-in agent
 * @returns {boolean}
 */
export const isPinnedToAnotherAgent = (pinnedByUsername, activeUser) => Boolean(pinnedByUsername) && pinnedByUsername !== activeUser;
